import { MapBuilder, px, type Decor, type Light, type MapDef } from '../mapdef';

/**
 * Beitou hot springs (北投溫泉), arriving at Xinbeitou MRT in the south-west. The steaming Beitou
 * Stream runs down the middle of Beitou Park from Thermal Valley (地熱谷, top right) past the
 * Millennium Hot Spring, the Hot Spring Museum and the green wooden library to the station plaza,
 * where the little historic Xinbeitou station stands. Green hills with hot-spring hotels rise on
 * both sides; Zhongshan Road climbs the east bank.
 */
const W = 80, H = 56;
const b = new MapBuilder(W, H, 'g');
// Hills all round (the valley opens to the south).
b.fill(0, 0, W, 2, 'M').fill(0, 0, 2, H, 'M').fill(W - 2, 0, 2, H, 'M').fill(0, H - 2, W, 2, 'h');
b.fill(2, 2, 3, 40, 'M').fill(75, 2, 3, 46, 'M');
for (let y = 2; y < 6; y++) b.fill(5, y, 20 - y * 3, 1, 'M');
// Station plaza along the bottom.
b.fill(2, 50, 58, 4, '7');
// Zhongshan Road up the east bank.
b.fill(60, 17, 4, 37, 'r');
b.fill(56, 50, 4, 4, '7');
// Thermal Valley: the jade pool, its rocky rim and the boardwalk.
b.fill(60, 2, 15, 12, 'M').fill(61, 11, 13, 2, 'o');
b.fill(62, 4, 11, 7, '9').fill(64, 3, 7, 1, '9').fill(61, 6, 1, 4, '9').fill(73, 5, 1, 4, '9').fill(65, 11, 6, 1, '9');
b.fill(58, 13, 17, 2, '8');
// The Millennium Hot Spring: three terraced pools inside a stone enclosure.
b.fill(39, 5, 17, 10, '7');
b.fill(41, 7, 4, 3, '9').fill(46, 7, 4, 3, '9').fill(51, 7, 3, 3, '9');
b.fill(42, 11, 10, 2, '9');
b.fill(39, 5, 17, 1, 'h').fill(39, 5, 1, 10, 'h').fill(55, 5, 1, 10, 'h');
b.fill(40, 13, 15, 1, '8');
b.fill(40, 14, 15, 1, 'h'); // the bath is entered through the ticket gate (an interaction)

// Park paths.
b.fill(12, 34, 34, 2, '7'); // library front walk, over the middle bridge
b.fill(10, 36, 3, 14, '7'); // up from the station
b.fill(46, 34, 14, 2, '7'); // museum front walk
b.fill(56, 15, 4, 20, '7'); // up to the egg stall and the valley
b.fill(30, 15, 26, 2, '7'); // Plum Garden to the Millennium Hot Spring
b.fill(30, 17, 2, 18, '7');
// Boardwalks: library porch and the foot bath deck.
b.fill(15, 33, 14, 1, '8');
b.fill(25, 39, 11, 4, '8');

// Beitou Stream: three tiles wide, flowing out past the Millennium Hot Spring and winding down to the plaza.
const course: [number, number][] = [];
for (let y = 15; y < 50; y++) {
  const cx = y < 22 ? 53 - (y - 15) * 1.2 : y < 38 ? 45 - Math.sin((y - 22) / 5) * 3 - ((y - 22) / 16) * 3 : 41 - (y - 38) * 0.35;
  course.push([Math.round(cx), y]);
}
for (const [cx, y] of course) b.fill(cx - 1, y, 3, 1, '9');
b.fill(37, 50, 3, 4, '9');
// Bridges.
const bridgeAt = (y: number) => {
  const cx = course.find((p) => p[1] === y)![0];
  b.fill(cx - 2, y, 5, 2, 'y');
};
bridgeAt(15);
bridgeAt(34);
bridgeAt(44);
b.fill(36, 50, 5, 2, 'y');
// Hillside terraces for the hotels.
b.fill(6, 8, 7, 2, '7').fill(6, 18, 7, 2, '7').fill(6, 28, 7, 2, '7');
b.fill(66, 24, 8, 2, '7').fill(66, 34, 8, 2, '7').fill(66, 44, 8, 2, '7');
b.fill(12, 9, 2, 26, '7');
b.fill(64, 25, 2, 2, '7').fill(64, 35, 2, 2, '7').fill(64, 45, 2, 2, '7');
// Flowerbeds.
b.fill(16, 42, 6, 2, 'G').fill(46, 40, 6, 2, 'G');

const at = (tx: number, ty: number) => ({ x: px(tx), y: px(ty) });
type TreeKind = 'tree' | 'cherry' | 'pine';
const tree = (tx: number, ty: number, kind: TreeKind = 'tree'): Decor => ({ kind, ...at(tx, ty), solid: { x: px(tx) - 6, y: px(ty) - 6, w: 12, h: 6 } });
const lamp = (tx: number, ty: number): Decor => ({ kind: 'ck_btLamp', ...at(tx, ty), solid: { x: px(tx) - 2, y: px(ty) - 3, w: 4, h: 3 } });
const steam = (tx: number, ty: number, v = 0): Decor => ({ kind: 'ck_steam', x: px(tx) + 8, y: px(ty) + 12, w: 40, variant: v });

const decor: Decor[] = [
  // --- Landmarks -----------------------------------------------------------------------------
  { kind: 'ck_mrtStation', x: px(9) + 8, y: px(50), w: 230, solid: { x: px(2), y: px(43), w: px(15), h: px(7) } },
  { kind: 'ck_oldStation', x: px(23) + 8, y: px(50), w: 200, solid: { x: px(18), y: px(45), w: px(12), h: px(5) } },
  { kind: 'ck_library', x: px(22), y: px(33), w: 260, solid: { x: px(15), y: px(25), w: px(14), h: px(8) } },
  { kind: 'ck_museum', x: px(50) + 8, y: px(34), w: 200, solid: { x: px(45), y: px(26), w: px(11), h: px(8) } },
  { kind: 'ck_bathers', x: px(47) + 8, y: px(13), w: px(16) },
  { kind: 'ck_plum', x: px(34), y: px(14), w: 140, solid: { x: px(30), y: px(10), w: px(8), h: px(4) } },
  { kind: 'ck_pavilion', x: px(50), y: px(46), solid: { x: px(48) + 6, y: px(46) - 8, w: px(3) - 12, h: 8 } },
  { kind: 'ck_railing', x: px(66) + 8, y: px(13) + 4, w: px(17) },
  { kind: 'ck_valleyGate', x: px(57) + 8, y: px(15), w: 70 },
  // Hot-spring hotels with Japanese roofs on the hillsides.
  ...([[9, 8, 0], [9, 18, 1], [9, 28, 2], [70, 24, 1], [70, 34, 0], [70, 44, 2]] as const).map(([tx, ty, v]): Decor => ({
    kind: 'ck_hotel', x: px(tx) + 8, y: px(ty), w: 130, variant: v, solid: { x: px(tx - 3), y: px(ty - 4), w: px(7), h: px(4) },
  })),
  // --- Steam ---------------------------------------------------------------------------------
  ...course.filter((_, i) => i % 4 === 1).map(([cx, y], i) => steam(cx, y, i % 3)),
  steam(37, 51, 1),
  steam(64, 6, 3), steam(69, 5, 4), steam(71, 9, 3), steam(66, 9, 4), steam(63, 9, 3), steam(68, 7, 3), steam(67, 10, 4),
  steam(42, 8, 0), steam(48, 8, 1), steam(52, 8, 2), steam(46, 11, 0),
  // --- Rocks and warning signs -----------------------------------------------------------------
  { kind: 'ck_rock', x: px(61) + 4, y: px(12), variant: 0 },
  { kind: 'ck_rock', x: px(74), y: px(11), variant: 1 },
  { kind: 'ck_rock', x: px(62), y: px(3), variant: 2 },
  { kind: 'ck_rock', x: px(39) + 6, y: px(38), variant: 1 },
  { kind: 'ck_rock', x: px(46), y: px(25), variant: 0 },
  { kind: 'ck_danger', x: px(71), y: px(14) - 2 },
  { kind: 'ck_danger', x: px(59), y: px(15), variant: 1 },
  { kind: 'ck_danger', x: px(40), y: px(28), variant: 1 },
  // --- Street furniture ----------------------------------------------------------------------
  lamp(15, 36), lamp(28, 36), lamp(12, 44), lamp(47, 36), lamp(57, 36), lamp(55, 22), lamp(33, 17), lamp(55, 48), lamp(24, 53), lamp(44, 53),
  { kind: 'museumBench', x: px(18), y: px(37), solid: { x: px(18) - 12, y: px(37) - 6, w: 24, h: 6 } },
  { kind: 'museumBench', x: px(52), y: px(37), solid: { x: px(52) - 12, y: px(37) - 6, w: 24, h: 6 } },
  { kind: 'trashBin', x: px(30), y: px(36) },
  { kind: 'trashBin', x: px(17), y: px(53) },
  { kind: 'vendingDeco', x: px(32) + 8, y: px(51), solid: { x: px(32), y: px(51) - 6, w: 16, h: 6 } },
  { kind: 'bikeRack', x: px(46), y: px(53) },
  ...[0, 1, 2, 3].map((i): Decor => ({ kind: 'scooter', x: px(48) + i * 18, y: px(52) + 10, variant: i % 2 })),
  ...[0, 1].map((i): Decor => ({ kind: 'scooter', x: px(61) + 6, y: px(22 + i * 14), variant: i % 2 })),
  { kind: 'ck_lanterns', x: px(47) + 8, y: px(16), w: px(10) },
  { kind: 'ck_lanterns', x: px(23) + 8, y: px(51), w: px(10), variant: 1 },
  // --- Trees ---------------------------------------------------------------------------------
  ...([[7, 4], [11, 13], [7, 23], [10, 31], [7, 37], [3, 46]] as const).map(([x, y]) => tree(x, y, 'pine')),
  ...([[67, 18], [73, 29], [67, 39], [73, 50], [70, 17]] as const).map(([x, y]) => tree(x, y, 'pine')),
  // Green hills: pines on the slopes around the valley and the west hillside.
  ...([[60, 3], [75, 4], [76, 12], [59, 9], [3, 8], [3, 16], [4, 26], [3, 34], [76, 22], [76, 36], [16, 3], [22, 2], [30, 2], [38, 2], [48, 2], [56, 2]] as const).map(([x, y]) => ({ kind: 'pine' as const, x: px(x) + 8, y: px(y) + 12 })),
  ...([[17, 38], [24, 37], [33, 33], [20, 46], [27, 47], [33, 45], [50, 38], [56, 39], [56, 44], [48, 22], [38, 28], [20, 15], [55, 19]] as const).map(([x, y]) => tree(x, y)),
  tree(15, 23, 'cherry'), tree(28, 23, 'cherry'), tree(57, 40, 'cherry'), tree(44, 46, 'cherry'), tree(28, 12, 'cherry'), tree(38, 14, 'cherry'),
  { kind: 'flowerbed', x: px(19), y: px(44) },
  { kind: 'flowerbed', x: px(49), y: px(42) },
  { kind: 'bushDeco', x: px(14), y: px(24) },
  { kind: 'bushDeco', x: px(29), y: px(24) },
  { kind: 'bushDeco', x: px(47), y: px(24) },
];

// --- Art pass: inns and tea houses, yukata strollers, signs, lanterns, maples, bamboo, stream rocks, haze ----
const yuk = (tx: number, ty: number, v: number): Decor => ({ kind: 'ck_btYukata', x: px(tx) + 8, y: px(ty) + 12, w: 24, variant: v });
const person = (tx: number, ty: number, v: number): Decor => ({ kind: 'xm_person', x: px(tx) + 8, y: px(ty) + 12, variant: v });
const toro = (tx: number, ty: number): Decor => ({ kind: 'ck_btToro', x: px(tx) + 8, y: px(ty) + 12, w: 16, solid: { x: px(tx) + 3, y: px(ty) + 8, w: 10, h: 4 } });
const maple = (tx: number, ty: number, v = 0): Decor => ({ kind: 'ck_btMaple', x: px(tx) + 8, y: px(ty) + 12, w: 52, variant: v, solid: { x: px(tx) + 5, y: px(ty) + 8, w: 6, h: 4 } });
const bamboo = (tx: number, ty: number, v = 0): Decor => ({ kind: 'ck_btBamboo', x: px(tx) + 8, y: px(ty) + 12, w: 24, variant: v, solid: { x: px(tx) + 3, y: px(ty) + 8, w: 10, h: 4 } });
const hydr = (tx: number, ty: number, v = 0): Decor => ({ kind: 'ck_btHydrangea', x: px(tx) + 8, y: px(ty) + 12, w: 24, variant: v, solid: { x: px(tx) + 2, y: px(ty) + 8, w: 12, h: 4 } });
const fern = (tx: number, ty: number, v = 0): Decor => ({ kind: 'tk_fern', x: px(tx) + 8, y: px(ty) + 12, variant: v % 3 });
const osign = (tx: number, ty: number, v: number): Decor => ({ kind: 'ck_btOnsenSign', x: px(tx) + 8, y: px(ty) + 12, w: 28, variant: v, solid: { x: px(tx) + 5, y: px(ty) + 8, w: 6, h: 4 } });
const wall = (tx: number, ty: number, w: number, v = 0): Decor => ({ kind: 'ck_btStoneWall', x: px(tx) + px(w) / 2, y: px(ty) + 12, w: px(w), variant: v, solid: { x: px(tx), y: px(ty), w: px(w), h: 12 } });
const sitters = (tx: number, ty: number, v: number): Decor => ({ kind: 'hc_sitters', x: px(tx) + 8, y: px(ty) + 12, w: 36, variant: v, solid: { x: px(tx) - 7, y: px(ty) + 2, w: 30, h: 10 } });
const rocks = (i: number, v: number): Decor => ({ kind: 'ck_btStreamRocks', x: px(course[i][0]) + 8, y: px(course[i][1]) + 10, w: 32, variant: v });

decor.push(
  // Station plaza: strollers in yukata, a signpost, onsen sign, maples, pots and a dog.
  yuk(13, 52, 1), yuk(28, 52, 6), yuk(41, 52, 11), yuk(20, 53, 16), yuk(50, 51, 22), person(36, 53, 7), person(45, 52, 30), person(15, 51, 12),
  { kind: 'ck_btSignpost', x: px(31) + 8, y: px(53) + 12, w: 40, solid: { x: px(31) + 5, y: px(53) + 8, w: 6, h: 4 } },
  osign(14, 50, 0), osign(53, 50, 3),
  maple(15, 46, 0), maple(34, 48, 1), maple(5, 45, 0),
  { kind: 'hc_flowerPot', x: px(22) + 8, y: px(52) + 12, w: 30, variant: 1, solid: { x: px(22) + 1, y: px(52) + 6, w: 14, h: 6 } },
  { kind: 'hc_flowerPot', x: px(42) + 8, y: px(53) + 12, w: 30, variant: 3, solid: { x: px(42) + 1, y: px(53) + 6, w: 14, h: 6 } },
  { kind: 'hc_dog', x: px(26) + 8, y: px(53) + 10, w: 20 },
  { kind: 'xy_pigeons', x: px(38) + 8, y: px(52) + 8, w: 40, variant: 6 },
  sitters(56, 52, 0),
  // The west hillside below the hotels: stone walls, bamboo, ferns, hydrangeas, lanterns up the path.
  wall(5, 36, 4, 0), wall(5, 42, 3, 1), wall(14, 24, 4, 0), wall(13, 9, 3, 1),
  bamboo(6, 40, 0), bamboo(3, 48, 1), bamboo(6, 33, 2), bamboo(14, 5, 0), bamboo(58, 4, 1), bamboo(76, 18, 0), bamboo(76, 41, 1),
  fern(7, 38), fern(4, 43, 1), fern(8, 48, 2), fern(14, 36, 1), fern(14, 44, 2), fern(6, 12, 1), fern(6, 27, 2), fern(75, 21, 1), fern(75, 31), fern(75, 47, 2), fern(60, 10), fern(74, 9, 1), fern(59, 5, 2),
  hydr(14, 38, 0), hydr(14, 47, 1), hydr(21, 44, 2), hydr(44, 42, 0), hydr(22, 24, 3), hydr(30, 24, 1), hydr(47, 25, 2), hydr(60, 45, 0), hydr(65, 23, 1),
  toro(9, 38), toro(13, 42), toro(9, 46), toro(13, 48), toro(29, 17), toro(32, 22), toro(29, 29), toro(31, 35), toro(55, 17), toro(55, 28), toro(29, 36), toro(44, 36),
  // Inns and tea houses: a tea house by the plum garden path, an inn above the library, a bathhouse by the station.
  { kind: 'ck_btJpHouse', x: px(36), y: px(26), w: 104, variant: 1, solid: { x: px(32), y: px(22), w: px(8), h: px(4) } },
  { kind: 'ck_btJpHouse', x: px(21), y: px(21), w: 104, variant: 2, solid: { x: px(17), y: px(17), w: px(8), h: px(4) } },
  { kind: 'ck_btJpHouse', x: px(44), y: px(48), w: 104, variant: 0, solid: { x: px(40), y: px(44), w: px(8), h: px(4) } },
  yuk(37, 27, 3), yuk(22, 22, 9), yuk(45, 49, 13), yuk(33, 20, 18),
  osign(40, 27, 2), osign(26, 22, 0),
  maple(29, 20, 1), maple(42, 24, 0), maple(50, 49, 1), maple(17, 29, 0),
  // The park: yukata strollers on the paths, sitters, more people, rocks in the stream.
  yuk(12, 40, 2), yuk(12, 46, 8), yuk(31, 30, 5), yuk(57, 30, 14), yuk(57, 20, 19), yuk(40, 34, 21), yuk(24, 34, 15),
  person(20, 35, 9), person(49, 35, 25), person(58, 24, 33), person(33, 16, 44), person(11, 44, 50),
  sitters(36, 37, 1), sitters(53, 37, 2), sitters(17, 35, 3),
  rocks(2, 0), rocks(6, 1), rocks(10, 0), rocks(14, 1), rocks(18, 0), rocks(23, 1), rocks(27, 0), rocks(31, 1),
  { kind: 'ck_btStreamRocks', x: px(38) + 8, y: px(52) + 10, w: 32, variant: 1 },
  { kind: 'ck_railing', x: px(22) + 8, y: px(33) + 2, w: px(12) },
  { kind: 'ck_railing', x: px(30) + 8, y: px(42) + 14, w: px(11) },
  osign(31, 38, 3), osign(46, 33, 0),
  { kind: 'ck_btSignpost', x: px(57) + 8, y: px(34) + 12, w: 40, solid: { x: px(57) + 5, y: px(34) + 8, w: 6, h: 4 } },
  { kind: 'ck_btSignpost', x: px(13) + 8, y: px(36) + 12, w: 40, solid: { x: px(13) + 5, y: px(36) + 8, w: 6, h: 4 } },
  { kind: 'flowerbed', x: px(23), y: px(44) }, { kind: 'flowerbed', x: px(52), y: px(42) }, { kind: 'flowerbed', x: px(34), y: px(18) },
  { kind: 'trashBin', x: px(47), y: px(36) }, { kind: 'trashBin', x: px(12), y: px(48) + 8 },
  { kind: 'xy_pigeons', x: px(50) + 8, y: px(35) + 10, w: 40, variant: 7 },
  // Millennium Hot Spring and Thermal Valley: haze over the jade pool, tourists on the boardwalk, signs.
  { kind: 'ck_btMist', x: px(67) + 8, y: px(9) + 12, w: 160, variant: 0 },
  { kind: 'ck_btMist', x: px(66) + 8, y: px(6) + 12, w: 140, variant: 1 },
  { kind: 'ck_btMist', x: px(68) + 8, y: px(12) + 8, w: 160, variant: 2 },
  { kind: 'ck_btMist', x: px(47) + 8, y: px(10) + 12, w: 120, variant: 3 },
  person(61, 13, 1), person(65, 13, 17), person(70, 13, 29), person(73, 13, 5), person(57, 20, 13), yuk(56, 10, 4), yuk(57, 13, 10),
  osign(63, 15, 1), osign(56, 8, 2),
  { kind: 'ck_btStreamRocks', x: px(63) + 8, y: px(11) + 8, w: 32, variant: 0 },
  { kind: 'ck_btStreamRocks', x: px(71) + 8, y: px(11) + 8, w: 32, variant: 1 },
  maple(58, 17, 1), maple(74, 16, 0),
  // East hillside: bamboo and ferns under the inns, a lantern at each terrace, a dog asleep.
  toro(65, 24), toro(65, 34), toro(65, 44), wall(66, 26, 8, 1), wall(66, 36, 8, 0), wall(66, 46, 8, 1),
  { kind: 'hc_dog', x: px(63) + 8, y: px(27) + 10, w: 20, variant: 1 },
  yuk(62, 30, 7), yuk(62, 40, 12),
);

const warm = '255,214,150';
const lights: Light[] = [
  ...([[15, 36], [28, 36], [12, 44], [47, 36], [57, 36], [55, 22], [33, 17], [55, 48], [24, 53], [44, 53]] as const).map(([tx, ty]) => ({ x: px(tx), y: px(ty) - 26, r: 56, color: warm })),
  ...([[9, 8], [9, 18], [9, 28], [70, 24], [70, 34], [70, 44]] as const).map(([tx, ty]) => ({ x: px(tx) + 8, y: px(ty) - 20, r: 70, color: '255,200,120' })),
  { x: px(22), y: px(31), r: 110, color: '255,226,160' },
  { x: px(50) + 8, y: px(31), r: 100, color: '255,214,150' },
  { x: px(47) + 8, y: px(12), r: 90, color: '255,200,140' },
  { x: px(9) + 8, y: px(47), r: 100, color: '210,235,255' },
  { x: px(23) + 8, y: px(48), r: 80, color: '255,214,150' },
  { x: px(67), y: px(8), r: 100, color: '160,255,210' },
  // Art pass: inns, onsen signs and stone lanterns.
  ...([[36, 22], [21, 17], [44, 44]] as const).map(([tx, ty]) => ({ x: px(tx), y: px(ty), r: 90, color: '255,214,150' })),
  ...([[14, 48], [53, 48], [40, 25], [26, 20], [31, 36], [46, 31], [63, 13], [56, 6]] as const).map(([tx, ty]) => ({ x: px(tx) + 12, y: px(ty) - 10, r: 36, color: '255,200,130' })),
  ...([[9, 38], [13, 42], [9, 46], [13, 48], [29, 17], [32, 22], [29, 29], [31, 35], [55, 17], [55, 28], [29, 36], [44, 36], [65, 24], [65, 34], [65, 44]] as const).map(([tx, ty]) => ({ x: px(tx) + 8, y: px(ty) - 8, r: 26, color: '255,214,150' })),
];

export const beitou: MapDef = {
  id: 'beitou',
  w: b.w,
  h: b.h,
  rows: b.rows(),
  indoor: false,
  theme: { wallFace: '#e9dcc3', wallLight: '#f6ecd8', wainscot: '#8a5c3a', wallTop: '#3a2a20' },
  doors: [],
  decor,
  areas: [
    { key: 'ck_valley', rect: { x: px(58), y: 0, w: px(20), h: px(15) } },
    { key: 'ck_millennium', rect: { x: px(38), y: px(4), w: px(19), h: px(12) } },
    { key: 'ck_plumGarden', rect: { x: px(28), y: px(6), w: px(10), h: px(10) } },
    { key: 'ck_library', rect: { x: px(12), y: px(22), w: px(19), h: px(14) } },
    { key: 'ck_museum', rect: { x: px(45), y: px(22), w: px(15), h: px(14) } },
    { key: 'ck_btStation', rect: { x: 0, y: px(43), w: px(60), h: px(13) } },
    { key: 'ck_hotels', rect: { x: px(64), y: px(16), w: px(14), h: px(34) } },
    { key: 'ck_hotels', rect: { x: px(5), y: px(2), w: px(9), h: px(32) } },
    { key: 'ck_park', rect: { x: 0, y: 0, w: px(W), h: px(H) } },
  ],
  lights,
  spots: { mrt: { x: px(9) + 8, y: px(52) + 8, facing: 'up' } },
};

/** The stream's centre line (tile coordinates), for NPC spots and tests. */
export const BEITOU_STREAM = course;
